import React, { useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Heading
} from '@homebotapp/hb-react-component-catalog'
import { useVerificationFlow } from '../VerificationFlowContext'

// Import all view components
import VerifyingView from './views/VerifyingView'
import UnverifiedView from './views/UnverifiedView'
import RemovePartnerView from './views/RemovePartnerView'
import AddPartnerView from './views/AddPartnerView'
import SendMessageView from './views/SendMessageView'
import { SlideOnMount } from '../../Animations'
import { defineMessages, useIntl } from 'react-intl'
import { StartPrequalView } from './views/StartPrequalView'
import SendPrequalView from './views/SendPrequalView'
import VerificationPrequalView from './views/VerificationPrequalView'
import { PartnerType } from '../types'

const MSG = defineMessages({
  addPartner: {
    id: 'VerificationFlowModal.addPartner',
    defaultMessage: 'Do you have a different {customerType} you would like to add?'
  },
  messageSent: {
    id: 'VerificationFlowModal.messageSent',
    defaultMessage: '✅ Message sent!'
  },
  getPrequalified: {
    id: 'VerificationFlowModal.getPrequalified',
    defaultMessage: 'Get Pre-Qualified'
  },
  removePartner: {
    id: 'VerificationFlowModal.removePartner',
    defaultMessage: 'Remove {customerType}?'
  }
})

/**
 * VerificationFlowModal Component
 *
 * A unified modal container that stays mounted throughout the verification flow.
 * It renders different content views based on the current state and handles
 * common modal behaviors (backdrop, closing, etc.).
 */
const VerificationFlowModal = () => {
  const intl = useIntl()
  const { state, dispatch } = useVerificationFlow()
  const { onComplete, currentStep, removedPartnerType, currentPartnerType, prequalOnly } = state
  // Only show modal for non-idle, non-messaging states
  const isOpen = !['idle', 'complete'].includes(currentStep)

  // Handle completion of the verification flow
  useEffect(() => {
    if (currentStep === 'complete' && onComplete) {
      dispatch({ type: 'RESET' })
      onComplete()
    }
  }, [currentStep, onComplete])

  // Determine if close button should be shown based on current step
  const showCloseButton = ['verified', 'unverified', 'partnerAdded'].includes(currentStep)

  const getCustomerTypeForTitle = (partnerType: PartnerType) => {
    switch (currentPartnerType) {
      case 'LoanOfficer':
        return 'Loan Officer'
      case 'RealEstateAgent':
        return 'Real Estate Agent'
      case 'InsuranceAgent':
        return 'Insurance Agent'
      default:
        return 'Real Estate Agent'
    }
  }

  // Determine modal title based on current step and partner type
  let title = ''
  switch (currentStep) {
    case 'addPartner':
      // Use removedPartnerType if available, otherwise use currentPartnerType
      const partnerTypeForTitle = removedPartnerType || currentPartnerType
      title = intl.formatMessage(MSG.addPartner, {
        customerType: getCustomerTypeForTitle(partnerTypeForTitle)
      })
      break
    case 'removePartner':
      title = intl.formatMessage(MSG.removePartner, {
        customerType: getCustomerTypeForTitle(currentPartnerType)
      })
      break
    case 'sendPrequal':
      title = intl.formatMessage(MSG.messageSent)
      break
    default:
      title = ''
  }

  /**
   * Handles the modal close button click
   */
  const handleClose = () => {
    dispatch({ type: 'CANCEL' })
  }

  // Component that renders null, but checks if the membership is verified and
  // fires the appropriate event to the flow machine
  if (currentStep === 'verifying') {
    return <VerifyingView />
  }

  if (currentStep === 'messaging') {
    return <SendMessageView />
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      data-qa='verification-flow-modal'
      data-testid='verification-flow-modal'
    >
      <ModalOverlay />
      <ModalContent
        maxW={{ base: '90%', md: '500px' }}
        mx='auto'
        data-qa='verification-modal-content'
        data-testid='verification-modal-content'
      >
        <ModalHeader data-qa='verification-modal-header' data-testid='verification-modal-header'>
          <Heading textAlign='left'>{title}</Heading>
        </ModalHeader>
        {showCloseButton && (
          <ModalCloseButton data-qa='verification-modal-close' data-testid='verification-modal-close' />
        )}
        <ModalBody pb={6} data-qa='verification-modal-body' data-testid='verification-modal-body'>
          {/* Conditionally render the appropriate view based on current step */}
          {currentStep === 'unverified' && (
            <SlideOnMount>
              <UnverifiedView />
            </SlideOnMount>
          )}
          {currentStep === 'removePartner' && (
            <SlideOnMount>
              <RemovePartnerView />
            </SlideOnMount>
          )}
          {currentStep === 'addPartner' && (
            <SlideOnMount>
              <AddPartnerView />
            </SlideOnMount>
          )}
          {currentStep === 'startPrequal' && (
            <SlideOnMount>
              <StartPrequalView />
            </SlideOnMount>
          )}
          {currentStep === 'sendPrequal' && (
            <SlideOnMount>
              <SendPrequalView />
            </SlideOnMount>
          )}
          {currentStep === 'verificationPrequal' && (
            <SlideOnMount>
              <VerificationPrequalView />
            </SlideOnMount>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default VerificationFlowModal
